
import { computed, defineComponent, onMounted, reactive, ref, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import Decimal from 'decimal.js'
import { formatDate, formatDecimal, formatThousands } from '@/utils'
import Container from '@/components/form/container/Container.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import { getAvailableAcceptance, acceptancePayment } from '@/apis/paymentSlip/paymentSlip'
import { PaymentSlipAcceptancePayment, AcceptancePaymentContent } from '@/model/paymentSlip/paymentSlipModel'
import { formatBanendFlag, formatSplitFlag } from '@/utils/acceptanceBillFormatUtil'
import SimpleTableForEdit from '@/components/table/simple/SimpleTableForEdit.vue'
import AcceptancePaymentSelectVendorBankDialog from '@/views/pay/AcceptancePaymentSelectVendorBankDialog.vue'
import { getByVendorNumber } from '@/apis/main-data/supplier'
import { SupplierBankModel } from '@/model/master-data/supplierModel'

export default defineComponent({
  name: 'AcceptancePaymentDialog',
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    dataId: {
      type: Number,
      required: true
    },
    isSub: {
      type: Boolean,
      required: true
    },
    workData: {
      type: Object,
      default: null
    }
  },
  components: { SimpleTableForEdit, SimpleTable, Container, AcceptancePaymentSelectVendorBankDialog },
  emits: ['update:modelValue', 'refresh'],
  setup(props, context) {
    const { t } = useI18n()
    // 使用 computed 实现双向绑定
    const dialogVisible = computed({
      get: () => props.modelValue,
      set: (value) => context.emit('update:modelValue', value)
    })
    const priorityAcceptanceBillBank = ref(null as SupplierBankModel | null)
    const paymentSlipTableStruct = [
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'companyBankName', label: t('pay.PaymentBank') },
      { prop: 'payee', label: t('pay.payee') },
      {
        prop: 'bankName',
        label: t('pay.ReceivingBank'),
        canEdit: 'true',
        notComplete: (row: any) => (row.status !== 2 && !row.isAccepted && !row.isSub)
      },
      {
        prop: 'amount',
        label: t('pay.PaymentAmount'),
        formatter: (row: any) => formatThousands(row.amount),
        align: 'right'
      },
      { prop: 'dueDate', label: t('order.cutOffDate'), formatter: (row: any) => formatDate(row.dueDate, 'yyyy-MM-dd') },
      {
        prop: 'paymentDate',
        label: t('pay.paymentDay'),
        formatter: (row: any) => formatDate(row.paymentDate, 'yyyy-MM-dd')
      }
    ]
    const acceptanceStruct = [
      { prop: 'draftNo', label: t('pay.draftNo') },
      { prop: 'splitFlag', label: t('pay.splitFlag'), formatter: (row: any) => formatSplitFlag(row.splitFlag) },
      {
        prop: 'issueDate',
        label: t('pay.issueDate'),
        formatter: (row: any) => formatDate(row.issueDate, 'yyyy-MM-dd')
      },
      { prop: 'dueDate', label: t('order.cutOffDate'), formatter: (row: any) => formatDate(row.dueDate, 'yyyy-MM-dd') },
      { prop: 'bankName', label: t('pay.draftBankName') },
      { prop: 'banendFlag', label: t('pay.banendFlag'), formatter: (row: any) => formatBanendFlag(row.banendFlag) },
      {
        prop: 'draftAmt',
        label: t('pay.draftAmt'),
        formatter: (row: any) => formatThousands(row.draftAmt),
        align: 'right'
      }
    ]
    const dataMap = reactive({
      tableData: Array<any>(),
      acceptance: Array<any>(),
      listLoading: true,
      columnStruct: paymentSlipTableStruct,
      acceptanceColumnStruct: acceptanceStruct,
      selectVendorBankDialogVisible: false,
      vendorNumber: '',
      vendorBankAccount: '',
      vendorBankCode: '',
      vendorBankName: '',
      vendorBankList: Array<SupplierBankModel>(),
      async getAcceptanceList() {
        dataMap.listLoading = true
        const _tableData = { ...props.workData }
        dataMap.tableData = [_tableData]
        dataMap.acceptance = []
        const res = await getAvailableAcceptance()
        if (res && res.data && res.data.length > 0) {
          dataMap.acceptance = res.data.filter(d => d.companyBank === props.workData.companyBank)
        }

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      async getSupplierBankList() {
        dataMap.listLoading = true
        const res = await getByVendorNumber(props.workData.vendorNumber)
        if (res && res.data && res.data.length > 0) {
          dataMap.vendorBankList = res.data
          priorityAcceptanceBillBank.value = res.data.find(d => d.isPriorityAcceptanceBill) || null
          if (priorityAcceptanceBillBank.value) {
            dataMap.tableData[0].bankName = priorityAcceptanceBillBank.value.name
          }
        } else {
          dataMap.vendorBankList = []
        }

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      handleEditEvent(row: any, itemProp: string) {
        if (itemProp === 'bankName') {
          if (priorityAcceptanceBillBank.value) {
            dataMap.vendorNumber = row.vendorNumber
            dataMap.vendorBankAccount = priorityAcceptanceBillBank.value.account
            dataMap.vendorBankCode = priorityAcceptanceBillBank.value.code
            dataMap.vendorBankName = priorityAcceptanceBillBank.value.name
          } else {
            dataMap.vendorNumber = row.vendorNumber
            dataMap.vendorBankAccount = row.bankAccount
            dataMap.vendorBankCode = row.bankCode
            dataMap.vendorBankName = row.bankName
          }
          dataMap.selectVendorBankDialogVisible = true
        }
      },
      async onDialogConfirm(bank: SupplierBankModel) {
        if (bank == null) {
          alert('选择的承兑票收款银行为空')
          return
        } else if (bank.account === null) {
          alert('选择的承兑票收款银行账号为空')
          return
        } else if (bank.code === null) {
          alert('选择的承兑票收款银行编号为空')
          return
        } else if (bank.name === null) {
          alert('选择的承兑票收款银行名称为空')
          return
        }
        priorityAcceptanceBillBank.value = bank
        dataMap.tableData[0].bankName = priorityAcceptanceBillBank.value.name
        dataMap.selectVendorBankDialogVisible = false
      }
    })
    const handleConfirm = async() => {
      const param: PaymentSlipAcceptancePayment = {
        isSubWork: props.isSub,
        workId: props.dataId,
        workSubId: props.dataId,
        contents: []
      }
      let enDrSeAccTid = props.workData.bankAccount
      let enDrSeBankNum = props.workData.bankCode
      let enDrSeBankName = props.workData.bankName
      if (priorityAcceptanceBillBank.value) {
        enDrSeAccTid = priorityAcceptanceBillBank.value.account
        enDrSeBankNum = priorityAcceptanceBillBank.value.code
        enDrSeBankName = priorityAcceptanceBillBank.value.name
      }
      for (const a of dataMap.acceptance) {
        if (a.useAmount && a.useAmount > 0) {
          const content: AcceptancePaymentContent = {
            id: a.id,
            draftNo: a.draftNo,
            draftRange: a.draftRange,
            draftAmt: a.draftAmt.toString(),
            enDrDraftAmt: a.useAmount, // 背书转让票据（包）金额
            enDoRseFlag: a.banendFlag, // 转让标记
            enDrSeAccTid: enDrSeAccTid, // 被背书银行账户
            enDrSeName: props.workData.vendorName, // 被背书账户名称
            enDrSeBankNum: enDrSeBankNum, // 被背书开户行号
            enDrSeBankName: enDrSeBankName // 被背书银行名称
          }
          param.contents.push(content)
        } else if (a.useAmount && a.useAmount <= 0) {
          ElMessage({
            message: t('pay.useAmountError'),
            type: 'error',
            duration: 2000
          })
          return
        }
      }
      let amt = '0'
      for (const c of param.contents) {
        if (new Decimal(c.draftAmt).lt(new Decimal(c.enDrDraftAmt))) {
          ElMessage({
            message: t('pay.useAmountError'),
            type: 'error',
            duration: 2000
          })
          return
        }
        amt = new Decimal(amt).plus(new Decimal(c.enDrDraftAmt)).toString()
      }
      if (param.contents.length < 1 || props.workData.amount.toString() !== amt) {
        ElMessage({
          message: t('pay.useAmountError'),
          type: 'error',
          duration: 2000
        })
        return
      }

      const res = await acceptancePayment(param)
      if (res?.data && res?.data > 0) {
        ElMessage({
          message: t('common.setSuccess'),
          type: 'success',
          duration: 2000
        })
        context.emit('refresh')
        dialogVisible.value = false
      } else {
        ElMessage({
          message: res?.msg,
          type: 'error',
          duration: 2000
        })
      }
    }
    const handleCancel = () => {
      dialogVisible.value = false
    }
    const handleInputEvent = (value: string | number, row: any) => {
      row.useAmount = formatDecimal(value, 2, true)
    }
    onMounted(() => {
      dataMap.getSupplierBankList()
      dataMap.getAcceptanceList()
    })
    return {
      t,
      dialogVisible,
      handleCancel,
      handleConfirm,
      handleInputEvent,
      ...toRefs(dataMap)
    }
  }
})
